import {css, useTheme} from '@mui/material';
import {GetStylesFunction, ConditionalWrapper} from '@wallaby-ui/core';
import {isEmpty} from 'lodash';
import React, {useEffect, useMemo, useRef} from 'react';
import {OverlayTrigger, Tooltip} from 'react-bootstrap';

import DynamicNode from 'src/js/containers/document/components/DynamicNode';
import {RenderedTextChunkState} from 'src/js/containers/document/document_slice';
import {Tag as TagModel} from 'src/js/model/tag';

interface OwnProps {
  node: TagModel;
  nodeData?: RenderedTextChunkState;
  forceTransparent?: boolean;
}

type Props = OwnProps;

export const ConceptText: React.FC<Props> = (props) => {
  const {children, node, nodeData, forceTransparent = false} = props;
  const backgroundColor = node.getTypeColor(
    nodeData?.selected,
    nodeData?.highlight,
    forceTransparent
  );

  const elementRef = useRef<HTMLSpanElement>(null);

  const theme = useTheme();
  const styles = getStyles({theme});

  useEffect(() => {
    if (nodeData?.scrollTo && elementRef.current) {
      elementRef.current.scrollIntoView({behavior: 'smooth'});
    }
  }, [nodeData?.scrollTo]);

  const elementStyle = useMemo(
    () => ({
      backgroundColor,

      color:
        backgroundColor === 'transparent'
          ? 'inherit'
          : theme?.palette.custom.coolGray['800'],
    }),
    [backgroundColor, theme]
  );

  return (
    <ConditionalWrapper
      condition={node.getTooltip()}
      // eslint-disable-next-line react/no-unstable-nested-components
      wrapper={(wrapperChildren) => (
        <OverlayTrigger
          overlay={<Tooltip id={'tooltip-top'}>{node.getTooltip()}</Tooltip>}
          placement='top'
        >
          {wrapperChildren}
        </OverlayTrigger>
      )}
    >
      <span
        ref={elementRef}
        style={elementStyle}
        id={`${node.getId()}`}
        data-start={DynamicNode.getStart(props)}
        data-end={DynamicNode.getEnd(props)}
        css={styles.element}
      >
        {React.Children.map(children, (child) => {
          const isNonEmpty = React.isValidElement(child) && !isEmpty(child);
          let ChildElement: React.ReactElement | undefined;
          if (isNonEmpty) {
            ChildElement = React.cloneElement(
              child,
              {
                forceTransparent: forceTransparent || !!nodeData?.highlight,
              },
              child.props.children
            );
          }
          return isNonEmpty && ChildElement ? ChildElement : null;
        })}
      </span>
    </ConditionalWrapper>
  );
};

ConceptText.displayName = 'ConceptText';

const getStyles: GetStylesFunction = (options) => ({
  element: css`
    border-radius: 3px;
    color: ${options?.theme?.palette.custom.coolGray['800']};
  `,
});
